import { FC } from 'react';
import { CustomButton, LoadingSpinner } from '@medtronic/pecc-react-component-library-js';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronDownIcon, CloseIcon } from '~/icons';
import { useTableFilters } from './context';
import { AutocompleteFilter, CheckboxFilter, DateFilter } from './filters';
import { FilterComponentProps, TableFilterType } from './filters/types';
import {
  closeIconStyles,
  collapseWrapperStyles,
  filtersListStyles,
  footerStyles,
  getCollapsePaperStyles,
  getContentStyles,
} from './styles';

interface TableFilterProps {
  open: boolean;
  onClose: () => void;
  height?: number | string;
  width?: number | string;
}

const tableFilterComponents: Record<TableFilterType, FC<FilterComponentProps>> = {
  [TableFilterType.CHECKBOX]: CheckboxFilter,
  [TableFilterType.DATE]: DateFilter,
  [TableFilterType.AUTOCOMPLETE]: AutocompleteFilter,
  [TableFilterType.MULTIPLE_AUTOCOMPLETE]: (props) => <AutocompleteFilter {...props} multiple />,
};

const TableFilter: FC<TableFilterProps> = ({ onClose, open, height = '50vh', width = 350 }) => {
  const { filterOptions, isFiltersLoading, applyFilters } = useTableFilters();

  const content = (
    <Box sx={getContentStyles(width, height)}>
      <Stack p={2} direction="column" spacing={1} className="HEADER">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="bodyXl" color="#170F5F">
            Filters
          </Typography>
          <IconButton sx={{ color: 'electricBlue.main' }} onClick={onClose}>
            <CloseIcon style={closeIconStyles} />
          </IconButton>
        </Stack>
      </Stack>
      <Divider />
      {isFiltersLoading ? (
        <Stack alignItems="center" p={2}>
          <LoadingSpinner spinnerSize="xs" />
        </Stack>
      ) : (
        <Stack sx={filtersListStyles}>
          {filterOptions?.map(({ name, type, id, options: values }) => {
            const TableFilterComponent = tableFilterComponents[type];

            return (
              <Accordion key={id} disableGutters elevation={0} defaultExpanded>
                <AccordionSummary expandIcon={<ChevronDownIcon fill="#1010eb" />}>
                  <Typography fontSize="1.8rem">{name}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableFilterComponent options={values} filterId={id} />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}
      <Divider />
      <Stack sx={footerStyles} alignItems="center">
        <CustomButton buttonType="outlined" onClick={applyFilters} fullWidth textTransform="none">
          Apply filter
        </CustomButton>
      </Stack>
    </Box>
  );

  return (
    <Collapse orientation="horizontal" in={open} sx={{ position: 'relative', zIndex: 2 }}>
      <Box sx={{ minHeight: '100%', backgroundColor: 'background.paper', position: 'relative', zIndex: 2 }}>
        <Box sx={collapseWrapperStyles}>
          <Paper className="PAPER" sx={getCollapsePaperStyles(width, height)}>
            {content}
          </Paper>
        </Box>
      </Box>
    </Collapse>
  );
};
export default TableFilter;
